import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Settings, LogOut, User, Menu, ChevronDown, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { CurrencySwitcher } from './CurrencySwitcher';
import { BrandLogo } from './BrandLogo';
import { SIGNUP_PATH } from '../lib/marketing';
import { track } from '../lib/analytics';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription,
} from './ui/sheet';

const NAV = [
  { label: 'Product', to: '/#product' },
  { label: 'Templates', to: '/templates' },
  { label: 'Features', to: '/features' },
  { label: 'Pricing', to: '/pricing' },
];
const RESOURCES = [
  { label: 'How it works', to: '/how-it-works' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Resume Builder India', to: '/resume-builder-india' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];
const MOBILE_NAV = [
  { label: 'Product', to: '/#product' }, { label: 'Templates', to: '/templates' }, { label: 'Features', to: '/features' },
  { label: 'Pricing', to: '/pricing' }, { label: 'How it works', to: '/how-it-works' }, { label: 'FAQ', to: '/faq' },
  { label: 'Resume Builder India', to: '/resume-builder-india' }, { label: 'About', to: '/about' }, { label: 'Contact', to: '/contact' },
];

const navCls = 'inline-flex min-h-[40px] items-center rounded-full px-3.5 text-[15px] font-medium text-primary/75 transition-colors hover:bg-muted hover:text-primary';

export const Brand = ({ to }) => {
  const { t } = useLanguage();
  return (
    <Link to={to} data-testid="brand-logo" className="flex min-h-[44px] shrink-0 items-center gap-2" aria-label="Careerova home">
      <span className="logo-mark flex h-9 w-9 items-center justify-center rounded-xl">
        <BrandLogo className="h-8 w-8 object-contain" />
      </span>
      <span className="logo-text text-[19px] font-bold tracking-tight">{t('brand.name')}</span>
    </Link>
  );
};

const UserMenu = ({ user, onLogout, t, navigate }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid="user-menu-trigger" aria-label="Account menu" className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user.name?.[0]?.toUpperCase() || <User className="h-4 w-4" />)}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-52">
      <div className="px-2 py-1.5">
        <p className="truncate text-sm font-semibold text-primary">{user.name}</p>
        <p className="truncate text-xs text-muted-foreground">{user.email || user.phone}</p>
      </div>
      <DropdownMenuSeparator />
      <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate('/dashboard')} className="cursor-pointer"><LayoutDashboard className="me-2 h-4 w-4" /> {t('nav.dashboard')}</DropdownMenuItem>
      <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate('/settings')} className="cursor-pointer"><Settings className="me-2 h-4 w-4" /> {t('nav.settings')}</DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem data-testid="menu-logout" onClick={onLogout} className="cursor-pointer text-destructive focus:text-destructive"><LogOut className="me-2 h-4 w-4" /> {t('nav.logout')}</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const MobileNav = ({ user, t, onLogout }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-trigger" aria-label="Open menu" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-white text-primary xl:hidden">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" data-testid="mobile-navigation" className="flex w-[340px] flex-col p-0">
        <div className="shrink-0 border-b border-border px-5 py-5">
          <SheetTitle className="text-base font-bold text-primary">{t('brand.name')}</SheetTitle>
          <SheetDescription className="sr-only">Site navigation</SheetDescription>
        </div>
        <nav aria-label="Mobile" className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain px-3 py-3">
          {MOBILE_NAV.map((l) => (
            <SheetClose asChild key={l.to}>
              <Link to={l.to} aria-current={location.pathname === l.to ? 'page' : undefined} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`} className={`flex min-h-[44px] items-center rounded-xl px-3 py-3 text-[15px] font-medium hover:bg-muted ${location.pathname === l.to ? 'bg-accent/10 text-accent' : 'text-primary'}`}>{l.label}</Link>
            </SheetClose>
          ))}
          <div className="mt-3 flex flex-wrap items-center gap-2 px-3"><CurrencySwitcher testId="mobile-currency" /><LanguageSwitcher persist={!!user} testId="mobile-language" /></div>
        </nav>
        <div className="safe-bottom shrink-0 border-t border-border p-4">
          {user ? (
            <div className="flex flex-col gap-2">
              <SheetClose asChild><Link to="/dashboard" data-testid="mobile-dashboard" className="rounded-full bg-primary px-4 py-3 text-center text-sm font-semibold text-primary-foreground">{t('nav.dashboard')}</Link></SheetClose>
              <SheetClose asChild><Link to="/settings" data-testid="mobile-settings" className="rounded-full border border-border px-4 py-3 text-center text-sm font-semibold text-primary">{t('nav.settings')}</Link></SheetClose>
              <button onClick={onLogout} data-testid="mobile-logout" className="rounded-full px-4 py-3 text-sm font-semibold text-destructive">{t('nav.logout')}</button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <SheetClose asChild><Link to="/login" data-testid="mobile-login" className="rounded-full border border-border px-4 py-3 text-center text-sm font-semibold text-primary">{t('nav.login')}</Link></SheetClose>
              <SheetClose asChild>
                <Link to={SIGNUP_PATH} data-testid="mobile-getstarted" onClick={() => track('hero_cta_click', { placement: 'mobile_nav' })} className="inline-flex items-center justify-center gap-2 rounded-full bg-accent px-4 py-3 text-sm font-semibold text-accent-foreground">Build My Resume <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
              </SheetClose>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// `showNavLinks` = public marketing chrome; app pages (dashboard/builder) get the minimal variant.
export const Header = ({ showNavLinks = false }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/70 bg-background/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1240px] items-center justify-between gap-2 px-4 sm:gap-4 sm:px-8">
        <Brand to={user ? '/dashboard' : '/'} />

        {showNavLinks && (
          <nav aria-label="Main" className="hidden items-center gap-1 xl:flex">
            {NAV.map((l) => <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className={navCls}>{l.label}</Link>)}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-resources" className={`${navCls} gap-1`}>Resources <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {RESOURCES.map((l) => (
                  <DropdownMenuItem key={l.to} asChild className="cursor-pointer"><Link to={l.to} data-testid={`nav-resources-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>{l.label}</Link></DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        )}

        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 lg:flex"><CurrencySwitcher compact /><LanguageSwitcher persist={!!user} /></div>
          {user ? (
            <div className="hidden lg:block"><UserMenu user={user} onLogout={handleLogout} t={t} navigate={navigate} /></div>
          ) : (
            <>
              <Link to="/login" data-testid="header-login" className="hidden min-h-[44px] items-center rounded-full px-3.5 text-[15px] font-medium text-primary/80 hover:text-primary xl:inline-flex">{t('nav.login')}</Link>
              <Link to={SIGNUP_PATH} data-testid="header-getstarted" onClick={() => track('hero_cta_click', { placement: 'header' })} className="hidden min-h-[40px] items-center gap-1.5 rounded-full bg-accent px-4 text-sm font-semibold text-accent-foreground transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#1D4ED8] sm:inline-flex">Build My Resume</Link>
            </>
          )}
          <MobileNav user={user} t={t} onLogout={handleLogout} />
        </div>
      </div>
    </header>
  );
};
